import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Database,
  Settings,
  X,
  BarChart3,
  PanelLeftClose, // Added: Icon for closing
  PanelLeftOpen, // Added: Icon for opening
} from "lucide-react";

interface SidebarProps {
  isOpen: boolean; // For mobile slide-out
  onClose: () => void; // For mobile slide-out
  isCollapsed: boolean; // For desktop collapse state
  onToggleSidebar: () => void; // For the button click
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  isCollapsed,
  onToggleSidebar,
}) => {
  const navigation = [
    {
      name: "Dashboards",
      href: "/dashboard",
      icon: LayoutDashboard,
    },
    {
      name: "Data Sources",
      href: "/data-sources",
      icon: Database,
    },
    {
      name: "Settings",
      href: "/settings",
      icon: Settings,
    },
  ];

  // Determine which icon to show for the toggle button
  const ToggleIcon = isCollapsed ? PanelLeftOpen : PanelLeftClose;

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Container */}
      <div
        className={`sidebar fixed inset-y-0 left-0 z-50 flex h-full flex-col border-r border-gray-200 bg-white transition-all duration-300 ease-in-out
          ${isCollapsed ? "w-20" : "w-64"}
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Header (Logo + Toggle Button) */}
          <div
            className={`flex items-center justify-between border-b border-gray-200 ${
              isCollapsed ? "px-2" : "px-6"
            } h-16`}
          >
            <div
              className={`flex items-center space-x-2 overflow-hidden ${
                isCollapsed ? "w-full justify-center" : ""
              }`}
            >
              <BarChart3 className="h-8 w-8 text-primary-600 flex-shrink-0" />
              <span
                className={`text-xl font-bold text-gray-900 ${
                  isCollapsed ? "hidden" : "block"
                } whitespace-nowrap`}
              >
                Insight
              </span>
            </div>

            {/* --- NEW/MOVED: Desktop Toggle Button (Visible only when not collapsed) --- */}
            <button
              type="button"
              className={`rounded-md p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors hidden lg:block ${
                isCollapsed ? "absolute right-0 translate-x-1/2" : ""
              }`}
              onClick={onToggleSidebar}
            >
              <ToggleIcon className="h-5 w-5" />
              <span className="sr-only">Toggle sidebar</span>
            </button>

            {/* Mobile Close Button */}
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav
            className={`flex-1 py-6 space-y-2 ${isCollapsed ? "px-2" : "px-4"}`}
          >
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={onClose} // Close sidebar on mobile nav click
              >
                {({ isActive }) => (
                  <span
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${
                        isActive
                          ? "bg-primary-100 text-primary-700"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      }
                      ${isCollapsed ? "justify-center" : ""}
                    `}
                  >
                    <item.icon
                      className={`h-5 w-5 flex-shrink-0
                        ${
                          isActive
                            ? "text-primary-500"
                            : "text-gray-400 group-hover:text-gray-500"
                        }
                        ${isCollapsed ? "mr-0" : "mr-3"}
                      `}
                    />
                    <span className={isCollapsed ? "hidden" : "block"}>
                      {item.name}
                    </span>
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Footer */}
          <div
            className={`p-4 border-t border-gray-200 ${
              isCollapsed ? "hidden" : "block"
            }`}
          >
            <div className="text-xs text-gray-500 text-center">
              <p>InsightDashboard v1.0</p>
              <p className="mt-1">AI-Powered Analytics</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
